import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import TrophyIcon from 'components/Icons/TrophyIcon';
import { LEADERBOARD_BANNER_ART, LEADERBOARD_BANNER_COPY, LEADERBOARD_ROWS } from 'constants/home';
import { useGetLeaderboardQuery } from 'store/services/leaderboard/leaderboardApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { LeaderboardCategory, LeaderboardEntry, LeaderboardPeriod, PERIOD_CONFIG } from 'types/interfaces/leaderboard';
import 'components/ArtCropFade/ArtCropFade.css';
import './HomeLeaderboard.css';

// The homepage Leaderboard card (Asana 1218957945501621, ruling 2026-09-28):
// most_cards_sent this month, up to LEADERBOARD_ROWS ranked rows, plain counts,
// and one static banner under them (Asana 1219036895682586: the conditional
// hint told ranked users to get on the board, so it is gone; the banner reads
// the same for every viewer). When the viewer is among the visible rows their
// row is highlighted in place; a separate You row appears only when they rank
// below the last visible row. Category-agnostic: the coin renders only for
// most_points, because card counts are not points (points display rule).

export interface HomeLeaderboardProps {
  category?: LeaderboardCategory;
  period?: LeaderboardPeriod;
}

/** The wire carries userId; id came back null on the real response, so match on either, then on the username. */
const entryUserId = (entry: LeaderboardEntry): number | null => entry.userId ?? (entry.id as number | null) ?? null;
const isMe = (entry: LeaderboardEntry, me: { id: number; username: string } | undefined): boolean =>
  !!me && (entryUserId(entry) === me.id || entry.username === me.username);

const Value: React.FC<{ value: number; category: LeaderboardCategory }> = ({ value, category }) =>
  category === 'most_points' ? <Points type="small" points={value} /> : <span className="home-lb__count">{value.toLocaleString('en-US')}</span>;

const HomeLeaderboard: React.FC<HomeLeaderboardProps> = ({ category = 'most_cards_sent', period = 'month' }) => {
  const { data, isLoading } = useGetLeaderboardQuery({ category, period, limit: LEADERBOARD_ROWS });
  const { data: me } = useGetMeQuery();

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const merged: LeaderboardEntry[] = [];
    [...(data?.podium ?? []), ...(data?.rankings ?? [])].forEach((entry) => {
      const key = `${entryUserId(entry) ?? ''}|${entry.username}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(entry);
    });
    return merged.sort((a, b) => a.rank - b.rank).slice(0, LEADERBOARD_ROWS);
  }, [data]);

  const current = data?.currentUser ?? null;
  const rank = current?.rank ?? null;
  const meInRows = rows.some((entry) => isMe(entry, me));
  const showYouRow = !!me && !meInRows;
  // A short board: the banner sits directly under the rows; only a full board
  // pins it to the bottom of the card (Decision 5)
  const filled = rows.length >= LEADERBOARD_ROWS;
  // Art that fails to load leaves a flat banner; the src never changes, so no loop
  const [artBroken, setArtBroken] = useState(false);

  return (
    <section className="home-lb" data-testid="home-leaderboard" data-category={category} data-period={period} data-rows={rows.length} data-me-in-rows={meInRows || undefined}>
      <div className="home-lb__header">
        <h2 className="home-lb__title">Leaderboard</h2>
        <Link to="/leaderboard" className="home-lb__period">
          {PERIOD_CONFIG[period].label}
        </Link>
      </div>
      <ol className="home-lb__list" aria-busy={isLoading || undefined}>
        {rows.map((entry) => {
          const mine = isMe(entry, me);
          return (
            <li key={`${entryUserId(entry) ?? ''}|${entry.username}`} className={`home-lb__row${mine ? ' home-lb__row--me' : ''}`} data-testid="home-lb-row" data-me={mine || undefined}>
              <span className="home-lb__rank">{entry.rank}</span>
              <AvatarWithFlag avatarUrl={entry.avatarUrl} username={entry.username} countryCode={entry.country} size={28} isElite={entry.isElite} showAnimatedBorder={entry.showAnimatedBorder} borderColor1={entry.borderColor1} borderColor2={entry.borderColor2} />
              <Link to={`/profile/u/${encodeURIComponent(entry.username)}`} className="home-lb__name">
                {mine ? 'You' : entry.username}
                <EliteBadge isElite={!!entry.isElite} />
              </Link>
              <span className="home-lb__value">
                <Value value={entry.displayValue} category={category} />
              </span>
            </li>
          );
        })}
        {!isLoading && rows.length === 0 && <li className="home-lb__empty">No trades on the board yet this month.</li>}
      </ol>
      {showYouRow && (
        <div className="home-lb__row home-lb__row--me home-lb__you" data-testid="home-lb-you">
          <span className="home-lb__rank">{rank !== null ? rank : '–'}</span>
          <AvatarWithFlag avatarUrl={me!.avatarUrl} username={me!.username} countryCode={me!.country} size={28} isElite={me!.isElite} showAnimatedBorder={me!.showAnimatedBorder} borderColor1={me!.borderColor1} borderColor2={me!.borderColor2} />
          <span className="home-lb__name">You</span>
          <span className="home-lb__value">{rank !== null && current ? <Value value={current.displayValue} category={category} /> : <span className="home-lb__unranked">unranked</span>}</span>
        </div>
      )}
      {/* Static, identical for every viewer and state: not a link, no hover.
          The art is decorative (alt empty); screen readers get only the copy. */}
      <div className={`home-lb__banner${filled ? ' home-lb__banner--pinned' : ''}`} data-testid="home-lb-banner" data-filled={filled || undefined}>
        <TrophyIcon className="home-lb__banner-trophy" size={22} strokeWidth={1.8} />
        <p className="home-lb__banner-copy">{LEADERBOARD_BANNER_COPY}</p>
        {!artBroken && (
          <img
            className="art-crop-fade home-lb__banner-art"
            src={LEADERBOARD_BANNER_ART}
            alt=""
            aria-hidden="true"
            loading="lazy"
            onError={() => setArtBroken(true)}
          />
        )}
      </div>
    </section>
  );
};

export default HomeLeaderboard;
